import React, { useState, useEffect } from 'react';
import api from '../services/api';
import DcaSettingsFields from '../components/masterchef/DcaSettingsFields';

const Toggle = ({ value, onChange, small }) => (
  <button
    onClick={() => onChange(!value)}
    className={`relative inline-flex items-center rounded-full transition-colors ${
      small ? 'h-5 w-9' : 'h-6 w-11'
    } ${value ? 'bg-green-600' : 'bg-gray-300'}`}
  >
    <span
      className={`inline-block transform rounded-full bg-white dark:bg-gray-800 transition-transform ${
        small ? 'h-3 w-3' : 'h-4 w-4'
      } ${value ? (small ? 'translate-x-5' : 'translate-x-6') : 'translate-x-1'}`}
    />
  </button>
);

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[11px] leading-tight text-gray-400 dark:text-gray-500">{hint}</p>}
  </div>
);

const inputClass = 'w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500';

const CHAINS = [
  { id: 'solana', label: 'Solana', unit: 'SOL' },
  { id: 'base', label: 'Base', unit: 'ETH' },
  { id: 'ethereum', label: 'Ethereum', unit: 'ETH' },
  { id: 'bsc', label: 'BSC', unit: 'BNB' },
  { id: 'robinhood', label: 'Robinhood', unit: 'ETH' },
];

const DEFAULT_SIZE_BY_CHAIN = {
  solana: 0.5,
  base: 0.05,
  ethereum: 0.05,
  bsc: 0.5,
  robinhood: 0.05,
};

const PUMP_DEFAULT_SIZE_BY_CHAIN = {
  solana: 0.15,
  base: 0.01,
  ethereum: 0.01,
  bsc: 0.1,
  robinhood: 0.01,
};

const DEFAULT_SETTINGS_VOLUME_TIERS = [
  { multiple: 2, sellPercent: 30 },
  { multiple: 3, sellPercent: 25 },
  { multiple: 5, sellPercent: 25 },
];

const ChainPositionSizes = ({ section, sizes, defaults, onChange }) => (
  <div className="col-span-2">
    <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-1.5">Position Size (native per chain)</p>
    <div className="grid grid-cols-2 gap-2">
      {CHAINS.map((chain) => (
        <div key={chain.id} className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 w-8">{chain.unit}</span>
          <input
            type="number"
            step="0.001"
            min="0.001"
            value={sizes?.[chain.id] ?? defaults[chain.id] ?? 0.05}
            onChange={(e) => onChange(chain.id, parseFloat(e.target.value) || 0)}
            className={inputClass}
          />
        </div>
      ))}
    </div>
    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
      Native amount bought per entry on each chain (capped by Max Position Size; split across DCA buys if enabled).
    </p>
  </div>
);

const Card = ({ title, subtitle, color = 'blue', children, badge, collapsible = true, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const colorMap = {
    blue: 'border-blue-200 dark:border-blue-800 bg-blue-50/80 dark:bg-blue-900/40',
    green: 'border-green-200 dark:border-green-800 bg-green-50/80 dark:bg-green-900/40',
    purple: 'border-purple-200 dark:border-purple-800 bg-purple-50/80 dark:bg-purple-900/40',
    orange: 'border-orange-200 dark:border-orange-800 bg-orange-50/80 dark:bg-orange-900/40',
    gray: 'border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80',
  };
  const headerColor = {
    blue: 'text-blue-900 dark:text-blue-200',
    green: 'text-green-900 dark:text-green-200',
    purple: 'text-purple-900 dark:text-purple-200',
    orange: 'text-orange-900 dark:text-orange-200',
    gray: 'text-gray-900 dark:text-white',
  };
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow border ${open ? '' : ''}`}>
      <div
        className={`flex items-center justify-between px-4 py-2.5 border-b rounded-t-lg ${colorMap[color]} cursor-pointer select-none`}
        onClick={() => collapsible && setOpen(!open)}
      >
        <div className="flex items-center gap-2">
          <h2 className={`text-sm font-semibold ${headerColor[color]}`}>{title}</h2>
          {badge}
        </div>
        <div className="flex items-center gap-2">
          {subtitle && <span className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</span>}
          {collapsible && (
            <svg className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
};

const SubSection = ({ title, children }) => (
  <div className="mb-4 last:mb-0">
    <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide mb-2">{title}</h3>
    {children}
  </div>
);

export default function MasterChefSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tradingWallets, setTradingWallets] = useState([]);
  const [walletBalances, setWalletBalances] = useState({});

  useEffect(() => {
    fetchSettings();
    fetchTradingWallets();
  }, []);

  const fetchTradingWallets = async () => {
    try {
      const response = await api.get('/trading/wallets');
      const activeWallets = response.data.filter((w) => w.isActive);
      setTradingWallets(activeWallets);
      // Fetch balances for all active wallets
      const activeIds = activeWallets.map(w => w._id);
      for (const w of activeWallets) {
        try {
          const balRes = await api.get(`/trading/wallets/${w._id}/balances`);
          setWalletBalances(prev => ({ ...prev, [w._id]: balRes.data.balances }));
        } catch { /* ignore */ }
      }
      // Auto-clear deactivated wallet assignments from settings
      setSettings(prev => {
        if (!prev?.wallets) return prev;
        let changed = false;
        const updated = { ...prev.wallets };
        for (const chainId of Object.keys(updated)) {
          if (updated[chainId]?.walletId && !activeIds.includes(updated[chainId].walletId)) {
            updated[chainId] = { ...updated[chainId], walletId: null };
            changed = true;
          }
        }
        return changed ? { ...prev, wallets: updated } : prev;
      });
    } catch (err) {
      console.error('Failed to fetch trading wallets:', err);
    }
  };

  const getWalletsForChain = (chainId) => {
    // Robinhood swaps use the shared EVM wallet key (schema stores `evm`, not `robinhood`).
    const matchChains = chainId === 'robinhood' ? ['robinhood', 'evm', 'ethereum', 'base', 'bsc'] : [chainId];
    return tradingWallets.filter((w) => (w.enabledChains || []).some((c) => matchChains.includes(c)));
  };

  const updateWalletSetting = (chainId, key, value) => {
    setSettings((prev) => ({
      ...prev,
      wallets: {
        ...prev.wallets,
        [chainId]: {
          ...prev.wallets?.[chainId],
          [key]: value,
        },
      },
    }));
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/masterchef/settings');
      setSettings(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch MC settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/masterchef/settings', settings);
      alert('Master Chef settings saved');
    } catch (err) {
      console.error('Failed to save MC settings:', err);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const update = (section, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  const updateChainPositionSize = (section, chainId, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        positionSizeByChain: {
          ...prev[section]?.positionSizeByChain,
          [chainId]: value,
        },
      },
    }));
  };

  const updateTier = (index, key, value) => {
    setSettings((prev) => {
      const tiers = [...(prev.exit?.tiers || [])];
      tiers[index] = { ...tiers[index], [key]: value };
      return { ...prev, exit: { ...prev.exit, tiers } };
    });
  };

  const addTier = () => {
    setSettings((prev) => {
      const tiers = [...(prev.exit?.tiers || [])];
      const lastMultiple = tiers.length > 0 ? tiers[tiers.length - 1].multiple : 1;
      tiers.push({ multiple: lastMultiple * 2, sellPercent: 25 });
      return { ...prev, exit: { ...prev.exit, tiers } };
    });
  };

  const removeTier = (index) => {
    setSettings((prev) => {
      const tiers = [...(prev.exit?.tiers || [])];
      tiers.splice(index, 1);
      return { ...prev, exit: { ...prev.exit, tiers } };
    });
  };

  const updatePumpTier = (index, key, value) => {
    setSettings((prev) => {
      const tiers = [...(prev.pumpExit?.tiers || [])];
      tiers[index] = { ...tiers[index], [key]: value };
      return { ...prev, pumpExit: { ...prev.pumpExit, tiers } };
    });
  };

  const addPumpTier = () => {
    setSettings((prev) => {
      const tiers = [...(prev.pumpExit?.tiers || [])];
      const lastMultiple = tiers.length > 0 ? tiers[tiers.length - 1].multiple : 1;
      tiers.push({ multiple: lastMultiple + 1, sellPercent: 25 });
      return { ...prev, pumpExit: { ...prev.pumpExit, tiers } };
    });
  };

  const removePumpTier = (index) => {
    setSettings((prev) => {
      const tiers = [...(prev.pumpExit?.tiers || [])];
      tiers.splice(index, 1);
      return { ...prev, pumpExit: { ...prev.pumpExit, tiers } };
    });
  };

  const updateVolumeTier = (index, key, value) => {
    setSettings((prev) => {
      const base = prev.volumeExit?.tiers?.length ? prev.volumeExit.tiers : DEFAULT_SETTINGS_VOLUME_TIERS;
      const tiers = [...base];
      tiers[index] = { ...tiers[index], [key]: value };
      return { ...prev, volumeExit: { ...prev.volumeExit, tiers } };
    });
  };

  const addVolumeTier = () => {
    setSettings((prev) => {
      const base = prev.volumeExit?.tiers?.length ? prev.volumeExit.tiers : DEFAULT_SETTINGS_VOLUME_TIERS;
      const tiers = [...base];
      const lastMultiple = tiers.length > 0 ? tiers[tiers.length - 1].multiple : 2;
      tiers.push({ multiple: lastMultiple + 2, sellPercent: 25 });
      return { ...prev, volumeExit: { ...prev.volumeExit, tiers } };
    });
  };

  const removeVolumeTier = (index) => {
    setSettings((prev) => {
      const base = prev.volumeExit?.tiers?.length ? prev.volumeExit.tiers : DEFAULT_SETTINGS_VOLUME_TIERS;
      const tiers = [...base];
      tiers.splice(index, 1);
      return { ...prev, volumeExit: { ...prev.volumeExit, tiers } };
    });
  };

  const updateBlacklist = (text) => {
    const tokens = text.split('\n').map(t => t.trim()).filter(Boolean);
    update('risk', 'blacklistedTokens', tokens);
  };

  const updateWhitelist = (text) => {
    const tokens = text.split('\n').map(t => t.trim()).filter(Boolean);
    update('risk', 'whitelistedTokens', tokens);
  };

  const updateVolumeBlacklist = (text) => {
    const tokens = text.split('\n').map(t => t.trim()).filter(Boolean);
    update('volumeRisk', 'blacklistedTokens', tokens);
  };

  const updateVolumeWhitelist = (text) => {
    const tokens = text.split('\n').map(t => t.trim()).filter(Boolean);
    update('volumeRisk', 'whitelistedTokens', tokens);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl">Loading Master Chef settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  const s = settings || {};

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Master Chef Settings</h1>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
            s.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600 dark:text-gray-400'
          }`}>
            {s.enabled ? 'Active' : 'Paused'}
          </span>
          {s.dryRun === false ? (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              LIVE
            </span>
          ) : (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:text-yellow-300">
              Dry Run
            </span>
          )}
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-5 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* ─── GENERAL SETTINGS ─── */}
      <Card title="General" subtitle="Core controls, wallets, monitoring & notifications" color="gray">
        {/* Enable / Dry Run */}
        <SubSection title="Controls">
          <div className="flex items-center gap-8 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Enable Master Chef</span>
              <Toggle value={s.enabled} onChange={(val) => setSettings(prev => ({ ...prev, enabled: val, status: val ? 'running' : 'paused' }))} />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Live Trading</span>
              <Toggle value={s.dryRun === false} onChange={(val) => {
                if (val) {
                  if (!window.confirm('Enable LIVE trading? This will execute real swaps with real funds on Solana mainnet. Are you sure?')) return;
                }
                setSettings(prev => ({ ...prev, dryRun: !val }));
              }} />
              {s.dryRun === false ? (
                <span className="flex items-center gap-1 text-xs font-semibold text-red-600">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  LIVE - Real funds
                </span>
              ) : (
                <span className="text-xs text-gray-400 dark:text-gray-500">Dry run (paper trading)</span>
              )}
            </div>
          </div>
          {s.dryRun === false && (
            <div className="mt-3 p-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
              <p className="text-xs font-semibold text-red-700 dark:text-red-400">LIVE MODE ENABLED</p>
              <p className="text-[10px] text-red-500 mt-0.5">All new positions will execute real swaps on enabled chains (Jupiter on Solana, Uniswap/Relay on EVM). Ensure wallets are funded per chain.</p>
            </div>
          )}
        </SubSection>

        {/* Wallets */}
        <SubSection title="Wallets">
          <div className="space-y-2">
            {CHAINS.map((chain) => {
              const wc = s.wallets?.[chain.id] || {};
              const chainWallets = getWalletsForChain(chain.id);
              const selectedWallet = tradingWallets.find(w => w._id === wc.walletId);
              const address = selectedWallet?.chains?.[chain.id]?.address;
              const bal = selectedWallet ? walletBalances[selectedWallet._id]?.[chain.id] : null;
              return (
                <div key={chain.id} className="flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="w-16 text-sm font-semibold text-gray-700 dark:text-gray-200">{chain.label}</div>
                  <div className="flex-1">
                    <select
                      value={wc.walletId || ''}
                      onChange={(e) => updateWalletSetting(chain.id, 'walletId', e.target.value || null)}
                      className={inputClass + ' bg-white dark:bg-gray-800'}
                    >
                      <option value="">Not assigned</option>
                      {chainWallets.map((w) => (
                        <option key={w._id} value={w._id}>{w.label}</option>
                      ))}
                    </select>
                    {address && (
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate">{address}</p>
                        {bal !== null && bal !== undefined && (
                          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                            bal > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                          }`}>
                            {bal.toFixed(4)} {chain.unit}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span className={`w-2 h-2 rounded-full ${wc.walletId ? 'bg-green-500' : 'bg-gray-300'}`} />
                </div>
              );
            })}
          </div>
          {!tradingWallets.length && (
            <p className="text-xs text-orange-600 mt-2">No trading wallets found. Create wallets in Wallet Management first.</p>
          )}
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">Assign a wallet per chain. Position size is set per strategy below (Position Size · native per chain).</p>
        </SubSection>

        {/* Monitoring, Execution & Notifications */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <SubSection title="Monitoring">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Recovery Scan (sec)">
                  <input type="number" min="5" max="120" value={s.monitoring?.recoveryScanIntervalSeconds || 10}
                    onChange={(e) => update('monitoring', 'recoveryScanIntervalSeconds', parseInt(e.target.value) || 10)}
                    className={inputClass} />
                </Field>
                <Field label="Position Check (sec)">
                  <input type="number" min="10" max="300" value={s.monitoring?.priceCheckIntervalSeconds || 30}
                    onChange={(e) => update('monitoring', 'priceCheckIntervalSeconds', parseInt(e.target.value) || 30)}
                    className={inputClass} />
                </Field>
                <Field label="Retry Failed TX">
                  <input type="number" min="0" max="10" value={s.monitoring?.retryFailedTxCount || 3}
                    onChange={(e) => update('monitoring', 'retryFailedTxCount', parseInt(e.target.value) || 3)}
                    className={inputClass} />
                </Field>
                <Field label="Max Slippage (%)">
                  <input type="number" step="0.5" min="0.5" max="20" value={s.monitoring?.maxSlippagePercent || 5}
                    onChange={(e) => update('monitoring', 'maxSlippagePercent', parseFloat(e.target.value) || 5)}
                    className={inputClass} />
                </Field>
              </div>
            </SubSection>
          </div>
          <div>
            <SubSection title="Execution">
              <div className="space-y-3">
                <div>
                  <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-1.5">Enabled Chains</p>
                  <div className="flex gap-2 flex-wrap">
                    {CHAINS.map((chain) => {
                      const enabled = (s.execution?.enabledChains || ['solana']).includes(chain.id);
                      return (
                        <button
                          key={chain.id}
                          onClick={() => {
                            const chains = s.execution?.enabledChains || ['solana'];
                            const next = enabled ? chains.filter(c => c !== chain.id) : [...chains, chain.id];
                            update('execution', 'enabledChains', next);
                          }}
                          className={`px-2 py-1 text-xs rounded-lg border font-medium transition-colors cursor-pointer ${
                            enabled
                              ? 'bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-400'
                              : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500'
                          }`}
                        >
                          {chain.label} {enabled ? '✓' : ''}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Click any chain to enable/disable. Solana, Base, Ethereum, and BSC supported — dry run on all; live needs funded wallets per chain.</p>
                </div>
                <Field label="EVM Swap Router">
                  <select
                    value={s.execution?.evmSwapRouter || 'uniswap'}
                    onChange={(e) => update('execution', 'evmSwapRouter', e.target.value)}
                    className={inputClass}
                  >
                    <option value="uniswap">Uniswap (recommended)</option>
                    <option value="relay">Relay.link</option>
                    <option value="0x">0x (when unpaused)</option>
                  </select>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Aggregator for Robinhood / Base / ETH / BSC. Default: Uniswap, then Relay fallback if Uniswap fails.</p>
                </Field>
                <Field label="Uniswap API Key (EVM swaps)">
                  <input type="password" value={s.execution?.uniswapApiKey || ''}
                    onChange={(e) => update('execution', 'uniswapApiKey', e.target.value)}
                    placeholder="Get from developers.uniswap.org (or set UNISWAP_API_KEY env)"
                    className={inputClass} />
                </Field>
                <Field label="Relay.link API Key (optional)">
                  <input type="password" value={s.execution?.relayApiKey || ''}
                    onChange={(e) => update('execution', 'relayApiKey', e.target.value)}
                    placeholder="Get from dashboard.relay.link (or set RELAY_API_KEY env)"
                    className={inputClass} />
                </Field>
                <Field label="0x API Key (keep for when unpaused)">
                  <input type="password" value={s.execution?.zeroXApiKey || ''}
                    onChange={(e) => update('execution', 'zeroXApiKey', e.target.value)}
                    placeholder="Get from 0x.org (or set ZEROX_API_KEY env)"
                    className={inputClass} />
                </Field>
                <Field label="Jupiter API Key">
                  <input type="password" value={s.execution?.jupiterApiKey || ''}
                    onChange={(e) => update('execution', 'jupiterApiKey', e.target.value)}
                    placeholder="Get from portal.jup.ag"
                    className={inputClass} />
                </Field>
                <Field label="Solana RPC URL">
                  <input type="text" value={s.execution?.rpcUrl || ''}
                    onChange={(e) => update('execution', 'rpcUrl', e.target.value)}
                    placeholder="Default public RPC"
                    className={inputClass} />
                </Field>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Jito MEV Protection</span>
                  <Toggle value={s.execution?.jitoEnabled !== false} onChange={(val) => update('execution', 'jitoEnabled', val)} small />
                </div>
                {s.execution?.jitoEnabled !== false && (
                  <Field label="Jito Tip (lamports)">
                    <input type="number" min="1000" step="1000" value={s.execution?.jitoTipLamports || 10000}
                      onChange={(e) => update('execution', 'jitoTipLamports', parseInt(e.target.value) || 10000)}
                      className={inputClass} />
                  </Field>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Confirm Timeout (ms)">
                    <input type="number" min="10000" step="5000" value={s.execution?.confirmTimeoutMs || 60000}
                      onChange={(e) => update('execution', 'confirmTimeoutMs', parseInt(e.target.value) || 60000)}
                      className={inputClass} />
                  </Field>
                  <Field label="Retry Attempts">
                    <input type="number" min="0" max="5" value={s.execution?.retryAttempts || 2}
                      onChange={(e) => update('execution', 'retryAttempts', parseInt(e.target.value) || 2)}
                      className={inputClass} />
                  </Field>
                </div>
              </div>
            </SubSection>
          </div>
          <div>
            <SubSection title="Notifications">
              <div className="mb-3">
                <Field label="Telegram Chat ID">
                  <input type="text" value={s.notifications?.telegramChatId || ''}
                    onChange={(e) => update('notifications', 'telegramChatId', e.target.value)}
                    placeholder="Chat ID"
                    className={inputClass} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {[
                  { key: 'onEntry', label: 'Position Opened' },
                  { key: 'onExitTier', label: 'Take-Profit Hit' },
                  { key: 'onStopLoss', label: 'Stop Loss Hit' },
                  { key: 'onTrailingStop', label: 'Trailing Stop' },
                  { key: 'onError', label: 'Errors' },
                ].map((item) => (
                  <div key={item.key} className="flex items-center gap-2 py-1">
                    <Toggle value={s.notifications?.[item.key]} onChange={(val) => update('notifications', item.key, val)} small />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
                  </div>
                ))}
              </div>
            </SubSection>
          </div>
        </div>
      </Card>

      {/* ─── STRATEGY CARDS: Recovery, Pump & Volume ─── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">

        {/* ─── RECOVERY STRATEGY ─── */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-green-200 dark:border-green-800 bg-green-50/80 dark:bg-green-900/40 rounded-t-lg">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-green-900 dark:text-green-200">Recovery Strategy</h2>
              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                s.entry?.enabled !== false ? 'bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-100' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}>
                {s.entry?.enabled !== false ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-300">Bottom recovery trades</span>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4 p-2.5 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-100 dark:border-green-800">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Enable Recovery Trading</span>
              <Toggle value={s.entry?.enabled !== false} onChange={(val) => update('entry', 'enabled', val)} />
            </div>
            {/* Entry */}
            <SubSection title="Entry">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Field label="Min Recovery (%)">
                  <input type="number" step="1" min="1" max="100" value={s.entry?.minRecoveryPercent || 20}
                    onChange={(e) => update('entry', 'minRecoveryPercent', parseInt(e.target.value) || 20)}
                    className={inputClass} />
                </Field>
                <Field label="Max Recovery (%)">
                  <input type="number" step="1" min="1" max="500" value={s.entry?.maxRecoveryPercent || 40}
                    onChange={(e) => update('entry', 'maxRecoveryPercent', parseInt(e.target.value) || 40)}
                    className={inputClass} />
                </Field>
                <Field label="Min Liquidity ($)">
                  <input type="number" step="1000" min="0" value={s.entry?.minLiquidity || 5000}
                    onChange={(e) => update('entry', 'minLiquidity', parseInt(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Min Volume 24h ($)">
                  <input type="number" step="500" min="0" value={s.entry?.minVolume24h || 1000}
                    onChange={(e) => update('entry', 'minVolume24h', parseInt(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Min Market Cap ($)">
                  <input type="number" step="5000" min="0" value={s.entry?.minMarketCap || 10000}
                    onChange={(e) => update('entry', 'minMarketCap', parseInt(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Max Market Cap ($)">
                  <input type="number" step="1000000" min="0" value={s.entry?.maxMarketCap || 50000000}
                    onChange={(e) => update('entry', 'maxMarketCap', parseInt(e.target.value) || 50000000)}
                    className={inputClass} />
                </Field>
                <Field label="Min Token Age (days)">
                  <input type="number" step="1" min="0" value={s.entry?.minTokenAgeDays ?? 1}
                    onChange={(e) => update('entry', 'minTokenAgeDays', parseInt(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Min 1h Change (%)">
                  <input type="number" step="1" value={s.entry?.minPriceChangeH1Percent ?? 0}
                    onChange={(e) => update('entry', 'minPriceChangeH1Percent', parseFloat(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Min Vol/MC Ratio">
                  <input type="number" step="0.1" min="0" value={s.entry?.minVolumeToMcRatio ?? 0.5}
                    onChange={(e) => update('entry', 'minVolumeToMcRatio', parseFloat(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
                <div className="col-span-2">
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                    Dead-token filters: <b>Min 1h Change</b> rejects tokens still dumping (0 = must be flat-or-up; set very negative to disable).
                    {' '}<b>Min Vol/MC</b> requires organic demand vs size (winners ~0.8, dead ~0.3; 0 = disable).
                  </p>
                </div>
                <ChainPositionSizes
                  section="entry"
                  sizes={s.entry?.positionSizeByChain}
                  defaults={DEFAULT_SIZE_BY_CHAIN}
                  onChange={(chainId, val) => updateChainPositionSize('entry', chainId, val)}
                />
                <div className="col-span-2">
                  <Field label="Max Position Size (ceiling, native)">
                    <input type="number" step="0.1" min="0" value={s.entry?.maxPositionSizeNative ?? 0}
                      onChange={(e) => update('entry', 'maxPositionSizeNative', parseFloat(e.target.value) || 0)}
                      className={inputClass} />
                  </Field>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                    Hard cap per trade. Must be ≥ your per-chain size above, or trades get silently shrunk to this. Set 0 to disable the cap.
                  </p>
                </div>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200">DCA</span>
                  <Toggle value={s.entry?.dcaEnabled} onChange={(val) => update('entry', 'dcaEnabled', val)} small />
                </div>
                {s.entry?.dcaEnabled && (
                  <DcaSettingsFields
                    values={s.entry}
                    inputClass={inputClass}
                    onUpdate={(key, val) => update('entry', key, val)}
                  />
                )}
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Pullback Mode</span>
                  <Toggle value={s.entry?.pullbackEnabled} onChange={(val) => update('entry', 'pullbackEnabled', val)} small />
                </div>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-2">
                  Stage late recoveries (above the window) and buy on a dip from the tracked peak.
                </p>
                {s.entry?.pullbackEnabled && (
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Stage Min Recovery (%)">
                      <input type="number" step="10" min="0" value={s.entry?.pullbackMinRecoveryPercent ?? (s.entry?.maxRecoveryPercent ?? 90)}
                        onChange={(e) => update('entry', 'pullbackMinRecoveryPercent', parseInt(e.target.value) || 0)}
                        className={inputClass} />
                    </Field>
                    <Field label="Stage Max Recovery (%)">
                      <input type="number" step="100" min="0" value={s.entry?.pullbackMaxRecoveryPercent ?? 3000}
                        onChange={(e) => update('entry', 'pullbackMaxRecoveryPercent', parseInt(e.target.value) || 3000)}
                        className={inputClass} />
                    </Field>
                    <Field label="Pullback From Peak (%)">
                      <input type="number" step="1" min="1" max="90" value={s.entry?.pullbackPercent ?? 20}
                        onChange={(e) => update('entry', 'pullbackPercent', parseInt(e.target.value) || 20)}
                        className={inputClass} />
                    </Field>
                    <Field label="Watch Timeout (hours)">
                      <input type="number" step="1" min="1" value={s.entry?.pullbackTimeoutHours ?? 24}
                        onChange={(e) => update('entry', 'pullbackTimeoutHours', parseInt(e.target.value) || 24)}
                        className={inputClass} />
                    </Field>
                    <Field label="Pullback Min Market Cap ($)">
                      <input type="number" step="1000" min="0" value={s.entry?.pullbackMinMarketCap ?? 5000}
                        onChange={(e) => update('entry', 'pullbackMinMarketCap', parseInt(e.target.value) || 0)}
                        className={inputClass} />
                    </Field>
                    <Field label="Pullback Max Market Cap ($)">
                      <input type="number" step="500000" min="0" value={s.entry?.pullbackMaxMarketCap ?? 5000000}
                        onChange={(e) => update('entry', 'pullbackMaxMarketCap', parseInt(e.target.value) || 5000000)}
                        className={inputClass} />
                    </Field>
                  </div>
                )}
              </div>
            </SubSection>

            {/* Exit Tiers */}
            <SubSection title="Exit Tiers">
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Take-profit levels</span>
                  <button onClick={addTier} className="text-xs text-green-600 hover:text-green-800 font-medium">+ Add</button>
                </div>
                <div className="space-y-1">
                  {(s.exit?.tiers || []).map((tier, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded">
                      <span className="text-xs text-gray-400 dark:text-gray-500 w-6">T{i + 1}</span>
                      <input type="number" step="0.5" min="1.5" value={tier.multiple}
                        onChange={(e) => updateTier(i, 'multiple', parseFloat(e.target.value) || 2)}
                        className={`${inputClass} w-16`} />
                      <span className="text-xs text-gray-400 dark:text-gray-500">x</span>
                      <input type="number" step="5" min="1" max="100" value={tier.sellPercent}
                        onChange={(e) => updateTier(i, 'sellPercent', parseInt(e.target.value) || 25)}
                        className={`${inputClass} w-14`} />
                      <span className="text-xs text-gray-400 dark:text-gray-500">%</span>
                      <button onClick={() => removeTier(i)} className="text-red-400 hover:text-red-600 text-xs ml-auto">x</button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Moon Bag (%)">
                  <input type="number" step="5" min="0" max="50" value={s.exit?.moonBagPercent || 10}
                    onChange={(e) => update('exit', 'moonBagPercent', parseInt(e.target.value) || 10)}
                    className={inputClass} />
                </Field>
                <Field label="Trailing Stop">
                  <div className="flex items-center gap-2 h-[34px]">
                    <Toggle value={s.exit?.trailingStopEnabled} onChange={(val) => update('exit', 'trailingStopEnabled', val)} small />
                    <span className="text-xs text-gray-500 dark:text-gray-400">{s.exit?.trailingStopEnabled ? 'On' : 'Off'}</span>
                  </div>
                </Field>
                {s.exit?.trailingStopEnabled && (
                  <>
                    <Field label="Trail Drop (%)">
                      <input type="number" step="5" min="5" max="80" value={s.exit?.trailingStopPercent || 30}
                        onChange={(e) => update('exit', 'trailingStopPercent', parseInt(e.target.value) || 30)}
                        className={inputClass} />
                    </Field>
                    <Field label="Trail After (x)">
                      <input type="number" step="0.5" min="1" value={s.exit?.trailingStopActivationMultiple || 2}
                        onChange={(e) => update('exit', 'trailingStopActivationMultiple', parseFloat(e.target.value) || 2)}
                        className={inputClass} />
                    </Field>
                  </>
                )}
              </div>
            </SubSection>

            {/* Risk */}
            <SubSection title="Risk">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Field label="Stop Loss (%)">
                  <input type="number" step="5" min="5" max="100" value={s.risk?.stopLossPercent || 50}
                    onChange={(e) => update('risk', 'stopLossPercent', parseInt(e.target.value) || 50)}
                    className={inputClass} />
                </Field>
                <Field label="Max Daily Trades">
                  <input type="number" min="1" max="50" value={s.risk?.maxDailyTrades || 5}
                    onChange={(e) => update('risk', 'maxDailyTrades', parseInt(e.target.value) || 5)}
                    className={inputClass} />
                </Field>
                <Field label="Max Concurrent">
                  <input type="number" min="1" max="50" value={s.risk?.maxConcurrentPositions || 10}
                    onChange={(e) => update('risk', 'maxConcurrentPositions', parseInt(e.target.value) || 10)}
                    className={inputClass} />
                </Field>
                <Field label="Max Per Chain">
                  <input type="number" min="1" max="20" value={s.risk?.maxConcurrentPerChain || 5}
                    onChange={(e) => update('risk', 'maxConcurrentPerChain', parseInt(e.target.value) || 5)}
                    className={inputClass} />
                </Field>
                <Field label="Loss Cooldown (min)">
                  <input type="number" min="0" max="1440" value={s.risk?.cooldownAfterLossMinutes || 30}
                    onChange={(e) => update('risk', 'cooldownAfterLossMinutes', parseInt(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Re-entry After Stop (h)" hint="Block re-buying the SAME token for N hours after it stops out. 0 = off.">
                  <input type="number" min="0" max="336" value={s.risk?.reentryAfterStopHours ?? 24}
                    onChange={(e) => update('risk', 'reentryAfterStopHours', parseInt(e.target.value, 10) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Max Stop-outs / Token" hint="Never re-enter a token once it has stopped out this many times. 0 = no cap.">
                  <input type="number" min="0" max="10" value={s.risk?.maxStopOutsPerToken ?? 2}
                    onChange={(e) => update('risk', 'maxStopOutsPerToken', parseInt(e.target.value, 10) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Max Daily Loss">
                  <input type="number" step="0.1" min="0" value={s.risk?.maxDailyLossNative || 1}
                    onChange={(e) => update('risk', 'maxDailyLossNative', parseFloat(e.target.value) || 1)}
                    className={inputClass} />
                </Field>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Toggle value={s.risk?.whitelistOnly} onChange={(val) => update('risk', 'whitelistOnly', val)} small />
                <span className="text-xs text-gray-700 dark:text-gray-300">Whitelist Only</span>
              </div>
              <div className="space-y-2">
                <Field label="Blacklisted Tokens (one per line)">
                  <textarea
                    value={(s.risk?.blacklistedTokens || []).join('\n')}
                    onChange={(e) => updateBlacklist(e.target.value)}
                    rows={2}
                    placeholder="Token addresses..."
                    className={`${inputClass} resize-none`}
                  />
                </Field>
                <Field label="Whitelisted Tokens (one per line)">
                  <textarea
                    value={(s.risk?.whitelistedTokens || []).join('\n')}
                    onChange={(e) => updateWhitelist(e.target.value)}
                    rows={2}
                    placeholder="Whitelist addresses..."
                    className={`${inputClass} resize-none`}
                  />
                </Field>
              </div>
            </SubSection>
          </div>
        </div>

        {/* ─── PUMP STRATEGY ─── */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-purple-200 dark:border-purple-800">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-purple-200 dark:border-purple-800 bg-purple-50/80 dark:bg-purple-900/40 rounded-t-lg">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-purple-900 dark:text-purple-200">Pump Strategy</h2>
              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                s.pumpEntry?.enabled ? 'bg-purple-200 dark:bg-purple-800 text-purple-900 dark:text-purple-100' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}>
                {s.pumpEntry?.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-300">Milestone momentum</span>
          </div>
          <div className="p-4">
            {/* Enable */}
            <div className="flex items-center gap-3 mb-4 p-2.5 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-100 dark:border-purple-800">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Enable Pump Trading</span>
              <Toggle value={s.pumpEntry?.enabled} onChange={(val) => update('pumpEntry', 'enabled', val)} />
            </div>

            {/* Entry */}
            <SubSection title="Entry">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Field label="Min Milestone (x)">
                  <input type="number" step="1" min="1" max="100" value={s.pumpEntry?.minMilestoneMultiplier || 2}
                    onChange={(e) => update('pumpEntry', 'minMilestoneMultiplier', parseInt(e.target.value) || 2)}
                    className={inputClass} />
                </Field>
                <Field label="Max Milestone (x)">
                  <input type="number" step="1" min="1" max="1000" value={s.pumpEntry?.maxMilestoneMultiplier || 20}
                    onChange={(e) => update('pumpEntry', 'maxMilestoneMultiplier', parseInt(e.target.value) || 20)}
                    className={inputClass} />
                </Field>
                <Field label="Entry Mode">
                  <select value={s.pumpEntry?.entryMode || 'immediate'}
                    onChange={(e) => update('pumpEntry', 'entryMode', e.target.value)}
                    className={inputClass}>
                    <option value="immediate">Immediate</option>
                    <option value="pullback">Pullback</option>
                  </select>
                </Field>
                <Field label="Min Token Age (days)">
                  <input type="number" step="0.5" min="0" value={s.pumpEntry?.minTokenAgeDays ?? 1}
                    onChange={(e) => update('pumpEntry', 'minTokenAgeDays', parseFloat(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
              </div>
              {(s.pumpEntry?.entryMode === 'pullback') && (
                <div className="grid grid-cols-2 gap-2 mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <Field label="Pullback (%)">
                    <input type="number" step="1" min="1" max="50" value={s.pumpEntry?.pullbackPercent || 15}
                      onChange={(e) => update('pumpEntry', 'pullbackPercent', parseInt(e.target.value) || 15)}
                      className={inputClass} />
                  </Field>
                  <Field label="Timeout (min)">
                    <input type="number" step="5" min="5" max="1440" value={s.pumpEntry?.pullbackTimeoutMinutes || 60}
                      onChange={(e) => update('pumpEntry', 'pullbackTimeoutMinutes', parseInt(e.target.value) || 60)}
                      className={inputClass} />
                  </Field>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Field label="Min Liquidity ($)">
                  <input type="number" step="1000" min="0" value={s.pumpEntry?.minLiquidity || 10000}
                    onChange={(e) => update('pumpEntry', 'minLiquidity', parseInt(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Min Market Cap ($)">
                  <input type="number" step="5000" min="0" value={s.pumpEntry?.minMarketCap || 50000}
                    onChange={(e) => update('pumpEntry', 'minMarketCap', parseInt(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Max Market Cap ($)">
                  <input type="number" step="1000000" min="0" value={s.pumpEntry?.maxMarketCap || 50000000}
                    onChange={(e) => update('pumpEntry', 'maxMarketCap', parseInt(e.target.value) || 50000000)}
                    className={inputClass} />
                </Field>
                <Field label="Min 24h Volume ($)">
                  <input type="number" step="1000" min="0" value={s.pumpEntry?.minVolume24h || 5000}
                    onChange={(e) => update('pumpEntry', 'minVolume24h', parseInt(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2 mb-2">
                <ChainPositionSizes
                  section="pumpEntry"
                  sizes={s.pumpEntry?.positionSizeByChain}
                  defaults={PUMP_DEFAULT_SIZE_BY_CHAIN}
                  onChange={(chainId, val) => updateChainPositionSize('pumpEntry', chainId, val)}
                />
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200">DCA</span>
                  <Toggle value={s.pumpEntry?.dcaEnabled} onChange={(val) => update('pumpEntry', 'dcaEnabled', val)} small />
                </div>
                {s.pumpEntry?.dcaEnabled && (
                  <DcaSettingsFields
                    values={s.pumpEntry}
                    inputClass={inputClass}
                    onUpdate={(key, val) => update('pumpEntry', key, val)}
                  />
                )}
              </div>
            </SubSection>

            {/* Exit Tiers */}
            <SubSection title="Exit Tiers">
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Tighter exits for momentum</span>
                  <button onClick={addPumpTier} className="text-xs text-purple-600 hover:text-purple-800 font-medium">+ Add</button>
                </div>
                <div className="space-y-1">
                  {(s.pumpExit?.tiers || []).map((tier, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded">
                      <span className="text-xs text-gray-400 dark:text-gray-500 w-6">T{i + 1}</span>
                      <input type="number" step="0.5" min="1" value={tier.multiple}
                        onChange={(e) => updatePumpTier(i, 'multiple', parseFloat(e.target.value) || 1)}
                        className={`${inputClass} w-16`} />
                      <span className="text-xs text-gray-400 dark:text-gray-500">x</span>
                      <input type="number" step="5" min="5" max="100" value={tier.sellPercent}
                        onChange={(e) => updatePumpTier(i, 'sellPercent', parseInt(e.target.value) || 25)}
                        className={`${inputClass} w-14`} />
                      <span className="text-xs text-gray-400 dark:text-gray-500">%</span>
                      <button onClick={() => removePumpTier(i)} className="text-red-400 hover:text-red-600 text-xs ml-auto">x</button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Moon Bag (%)">
                  <input type="number" step="5" min="0" max="50" value={s.pumpExit?.moonBagPercent || 10}
                    onChange={(e) => update('pumpExit', 'moonBagPercent', parseInt(e.target.value) || 10)}
                    className={inputClass} />
                </Field>
                <Field label="Trailing Stop">
                  <div className="flex items-center gap-2 h-[34px]">
                    <Toggle value={s.pumpExit?.trailingStopEnabled !== false} onChange={(val) => update('pumpExit', 'trailingStopEnabled', val)} small />
                    <span className="text-xs text-gray-500 dark:text-gray-400">{s.pumpExit?.trailingStopEnabled !== false ? 'On' : 'Off'}</span>
                  </div>
                </Field>
                {s.pumpExit?.trailingStopEnabled !== false && (
                  <>
                    <Field label="Trailing Stop (%)">
                      <input type="number" step="5" min="5" max="80" value={s.pumpExit?.trailingStopPercent || 25}
                        onChange={(e) => update('pumpExit', 'trailingStopPercent', parseInt(e.target.value) || 25)}
                        className={inputClass} />
                    </Field>
                    <Field label="Activation (x)">
                      <input type="number" step="0.5" min="1" value={s.pumpExit?.trailingStopActivationMultiple || 1.5}
                        onChange={(e) => update('pumpExit', 'trailingStopActivationMultiple', parseFloat(e.target.value) || 1.5)}
                        className={inputClass} />
                    </Field>
                  </>
                )}
              </div>
            </SubSection>

            {/* Risk */}
            <SubSection title="Risk">
              <div className="grid grid-cols-2 gap-2">
                <Field label="Stop Loss (%)">
                  <input type="number" step="5" min="5" max="100" value={s.pumpRisk?.stopLossPercent || 30}
                    onChange={(e) => update('pumpRisk', 'stopLossPercent', parseInt(e.target.value) || 30)}
                    className={inputClass} />
                </Field>
                <Field label="Max Daily Trades">
                  <input type="number" min="1" max="20" value={s.pumpRisk?.maxDailyPumpTrades || 3}
                    onChange={(e) => update('pumpRisk', 'maxDailyPumpTrades', parseInt(e.target.value) || 3)}
                    className={inputClass} />
                </Field>
                <Field label="Max Concurrent">
                  <input type="number" min="1" max="20" value={s.pumpRisk?.maxConcurrentPumpPositions || 5}
                    onChange={(e) => update('pumpRisk', 'maxConcurrentPumpPositions', parseInt(e.target.value) || 5)}
                    className={inputClass} />
                </Field>
                <Field label="Loss Cooldown (min)">
                  <input type="number" min="0" max="1440" value={s.pumpRisk?.cooldownAfterLossMinutes || 60}
                    onChange={(e) => update('pumpRisk', 'cooldownAfterLossMinutes', parseInt(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Blacklist/whitelist from recovery also applies.</p>
            </SubSection>
          </div>
        </div>

        {/* ─── VOLUME STRATEGY ─── */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-orange-200 dark:border-orange-800">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-orange-200 dark:border-orange-800 bg-orange-50/80 dark:bg-orange-900/40 rounded-t-lg">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-orange-900 dark:text-orange-200">Volume Strategy</h2>
              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                s.volumeEntry?.enabled !== false ? 'bg-orange-200 dark:bg-orange-800 text-orange-900 dark:text-orange-100' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}>
                {s.volumeEntry?.enabled !== false ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-300">Volume spike entries</span>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4 p-2.5 bg-orange-50 dark:bg-orange-900/30 rounded-lg border border-orange-100 dark:border-orange-800">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Enable Volume Trading</span>
              <Toggle value={s.volumeEntry?.enabled !== false} onChange={(val) => update('volumeEntry', 'enabled', val)} />
            </div>

            <SubSection title="Entry">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Field label="Min Recovery (%)">
                  <input type="number" step="1" min="0" value={s.volumeEntry?.minRecoveryPercent ?? 5}
                    onChange={(e) => update('volumeEntry', 'minRecoveryPercent', parseInt(e.target.value) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Max Recovery (%)">
                  <input type="number" step="1" min="1" value={s.volumeEntry?.maxRecoveryPercent ?? 500}
                    onChange={(e) => update('volumeEntry', 'maxRecoveryPercent', parseInt(e.target.value) || 500)}
                    className={inputClass} />
                </Field>
                <Field label="Volume Multiple (5m)">
                  <input type="number" step="0.5" min="1" value={s.volumeEntry?.volumeMultipleThreshold ?? 3}
                    onChange={(e) => update('volumeEntry', 'volumeMultipleThreshold', parseFloat(e.target.value) || 3)}
                    className={inputClass} />
                </Field>
                <Field label="Buy Ratio (%)">
                  <input type="number" step="1" min="50" max="100" value={s.volumeEntry?.buyRatioThreshold ?? 65}
                    onChange={(e) => update('volumeEntry', 'buyRatioThreshold', parseInt(e.target.value) || 65)}
                    className={inputClass} />
                </Field>
                <Field label="Min Vol 5m ($)">
                  <input type="number" step="100" min="0" value={s.volumeEntry?.minVolume5m ?? 500}
                    onChange={(e) => update('volumeEntry', 'minVolume5m', parseInt(e.target.value) || 500)}
                    className={inputClass} />
                </Field>
                <Field label="Auto-Entry Score">
                  <input type="number" step="5" min="0" max="100" value={s.volumeEntry?.autoEntryScoreThreshold ?? 70}
                    onChange={(e) => update('volumeEntry', 'autoEntryScoreThreshold', parseInt(e.target.value) || 70)}
                    className={inputClass} />
                </Field>
                <ChainPositionSizes
                  section="volumeEntry"
                  sizes={s.volumeEntry?.positionSizeByChain}
                  defaults={DEFAULT_SIZE_BY_CHAIN}
                  onChange={(chainId, val) => updateChainPositionSize('volumeEntry', chainId, val)}
                />
                <Field label="Execution Mode">
                  <select value={s.volumeEntry?.executionMode || 'dry_run'}
                    onChange={(e) => update('volumeEntry', 'executionMode', e.target.value)}
                    className={inputClass}>
                    <option value="dry_run">Dry Run</option>
                    <option value="live">Live</option>
                  </select>
                </Field>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200">DCA</span>
                  <Toggle value={s.volumeEntry?.dcaEnabled} onChange={(val) => update('volumeEntry', 'dcaEnabled', val)} small />
                </div>
                {s.volumeEntry?.dcaEnabled && (
                  <DcaSettingsFields
                    values={s.volumeEntry}
                    inputClass={inputClass}
                    onUpdate={(key, val) => update('volumeEntry', key, val)}
                    hint="Volume spikes often rip then retrace — drop-DCA adds on pullback instead of chasing the spike."
                  />
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Scans recovery tokens for volume spikes.{' '}
                <a href="/trading/volume-monitor" className="text-orange-600 dark:text-orange-400 hover:underline">View alerts →</a>
              </p>
            </SubSection>

            <SubSection title="Exit Tiers">
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Take-profit levels for volume trades</span>
                  <button type="button" onClick={addVolumeTier} className="text-xs text-orange-600 hover:text-orange-800 font-medium">+ Add</button>
                </div>
                <div className="space-y-1">
                  {(s.volumeExit?.tiers || DEFAULT_SETTINGS_VOLUME_TIERS).map((tier, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded">
                      <span className="text-xs text-gray-400 dark:text-gray-500 w-6">T{i + 1}</span>
                      <input type="number" step="0.5" min="1.5" value={tier.multiple}
                        onChange={(e) => updateVolumeTier(i, 'multiple', parseFloat(e.target.value) || 2)}
                        className={`${inputClass} w-16`} />
                      <span className="text-xs text-gray-400 dark:text-gray-500">x</span>
                      <input type="number" step="5" min="1" max="100" value={tier.sellPercent}
                        onChange={(e) => updateVolumeTier(i, 'sellPercent', parseInt(e.target.value, 10) || 25)}
                        className={`${inputClass} w-14`} />
                      <span className="text-xs text-gray-400 dark:text-gray-500">%</span>
                      <button type="button" onClick={() => removeVolumeTier(i)} className="text-red-400 hover:text-red-600 text-xs ml-auto">×</button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Moon Bag (%)">
                  <input type="number" step="5" min="0" max="50" value={s.volumeExit?.moonBagPercent ?? 30}
                    onChange={(e) => update('volumeExit', 'moonBagPercent', parseInt(e.target.value, 10) || 10)}
                    className={inputClass} />
                </Field>
                <Field label="Trailing Stop">
                  <div className="flex items-center gap-2 h-[34px]">
                    <Toggle value={s.volumeExit?.trailingStopEnabled !== false} onChange={(val) => update('volumeExit', 'trailingStopEnabled', val)} small />
                    <span className="text-xs text-gray-500 dark:text-gray-400">{s.volumeExit?.trailingStopEnabled !== false ? 'On' : 'Off'}</span>
                  </div>
                </Field>
                {s.volumeExit?.trailingStopEnabled !== false && (
                  <>
                    <Field label="Trail Drop (%)">
                      <input type="number" step="5" min="5" max="90" value={s.volumeExit?.trailingStopPercent ?? 90}
                        onChange={(e) => update('volumeExit', 'trailingStopPercent', parseInt(e.target.value, 10) || 30)}
                        className={inputClass} />
                    </Field>
                    <Field label="Trail After (x)">
                      <input type="number" step="0.5" min="1" value={s.volumeExit?.trailingStopActivationMultiple ?? 10}
                        onChange={(e) => update('volumeExit', 'trailingStopActivationMultiple', parseFloat(e.target.value) || 10)}
                        className={inputClass} />
                    </Field>
                  </>
                )}
              </div>
            </SubSection>

            <SubSection title="Risk">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Field label="Stop Loss (%)">
                  <input type="number" step="5" min="5" max="100" value={s.volumeRisk?.stopLossPercent ?? 25}
                    onChange={(e) => update('volumeRisk', 'stopLossPercent', parseInt(e.target.value, 10) || 25)}
                    className={inputClass} />
                </Field>
                <Field label="Max Daily Trades">
                  <input type="number" min="1" max="50" value={s.volumeRisk?.maxDailyVolumeTrades ?? 10}
                    onChange={(e) => update('volumeRisk', 'maxDailyVolumeTrades', parseInt(e.target.value, 10) || 10)}
                    className={inputClass} />
                </Field>
                <Field label="Max Concurrent">
                  <input type="number" min="1" max="50" value={s.volumeRisk?.maxConcurrentVolumePositions ?? 12}
                    onChange={(e) => update('volumeRisk', 'maxConcurrentVolumePositions', parseInt(e.target.value, 10) || 12)}
                    className={inputClass} />
                </Field>
                <Field label="Max Per Chain">
                  <input type="number" min="1" max="20" value={s.volumeRisk?.maxConcurrentVolumePerChain ?? 10}
                    onChange={(e) => update('volumeRisk', 'maxConcurrentVolumePerChain', parseInt(e.target.value, 10) || 10)}
                    className={inputClass} />
                </Field>
                <Field label="Loss Cooldown (min)">
                  <input type="number" min="0" max="1440" value={s.volumeRisk?.cooldownAfterLossMinutes ?? 60}
                    onChange={(e) => update('volumeRisk', 'cooldownAfterLossMinutes', parseInt(e.target.value, 10) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Re-entry Cooldown (min)">
                  <input type="number" min="0" max="1440" value={s.volumeRisk?.reentryCooldownMinutes ?? 120}
                    onChange={(e) => update('volumeRisk', 'reentryCooldownMinutes', parseInt(e.target.value, 10) || 0)}
                    className={inputClass} />
                </Field>
                <Field label="Max Daily Loss">
                  <input type="number" step="0.1" min="0" value={s.volumeRisk?.maxDailyLossNative ?? 1}
                    onChange={(e) => update('volumeRisk', 'maxDailyLossNative', parseFloat(e.target.value) || 1)}
                    className={inputClass} />
                </Field>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Toggle value={s.volumeRisk?.whitelistOnly} onChange={(val) => update('volumeRisk', 'whitelistOnly', val)} small />
                <span className="text-xs text-gray-700 dark:text-gray-300">Whitelist Only</span>
              </div>
              <div className="space-y-2">
                <Field label="Blacklisted Tokens (one per line)">
                  <textarea
                    value={(s.volumeRisk?.blacklistedTokens || s.risk?.blacklistedTokens || []).join('\n')}
                    onChange={(e) => updateVolumeBlacklist(e.target.value)}
                    rows={2}
                    placeholder="Token addresses..."
                    className={`${inputClass} resize-none`}
                  />
                </Field>
                <Field label="Whitelisted Tokens (one per line)">
                  <textarea
                    value={(s.volumeRisk?.whitelistedTokens || s.risk?.whitelistedTokens || []).join('\n')}
                    onChange={(e) => updateVolumeWhitelist(e.target.value)}
                    rows={2}
                    placeholder="Whitelist addresses..."
                    className={`${inputClass} resize-none`}
                  />
                </Field>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Volume uses its own limits. Empty lists fall back to recovery risk lists.
              </p>
            </SubSection>
          </div>
        </div>

      </div>
    </div>
  );
}
